import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Consistencies } from 'app/modules/sho_management_pro/sales/store/sales.models';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import * as SaleActions from '../../sho_management_pro/sales/store/sales.actions';
import { NotificationAdapterService } from '../services/notification-adapter.service';
import { NotificationApiService } from '../services/notification-api.service';
import { NotificationBuilderService } from '../services/notification-builder.service';
import * as NotificationActions from './notification.actions';

@Injectable()
export class NotificationsEffects {
    private _actions$ = inject(Actions);
    private _notificationApiService = inject(NotificationApiService);
    private _notificationAdapter = inject(NotificationAdapterService);
    private _notificationBuilder = inject(NotificationBuilderService);
    private _alertService = inject(AlertService);

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    /**
     * Fetches all notifications from the API and replaces the current state.
     */
    loadNotifications$ = createEffect(() =>
        this._actions$.pipe(
            ofType(NotificationActions.loadNotifications),
            switchMap(() =>
                this._notificationApiService.getAll().pipe(
                    map((res) => {
                        return NotificationActions.loadNotificationsSuccess({
                            notifications: this._notificationAdapter.adaptAll(
                                res.data
                            ),
                            pagination: {
                                lastPage: res.last_page,
                                page: res.page,
                                total: res.total,
                            },
                        });
                    }),
                    catchError((error) =>
                        of(
                            NotificationActions.loadNotificationsFailure({
                                error: error?.error?.message ?? 'Unknown error',
                            })
                        )
                    )
                )
            )
        )
    );

    createNotification$ = createEffect(() =>
        this._actions$.pipe(
            ofType(NotificationActions.createNotification),
            switchMap((action) =>
                this._notificationApiService.create(action.payload).pipe(
                    map((res) => {
                        return NotificationActions.createNotificationSuccess({
                            data: this._notificationAdapter.adaptSingle(
                                res.data.notification
                            ),
                            success: 'Notification created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            NotificationActions.createNotificationFailure({
                                error: error?.error?.message ?? 'Unknown error',
                            })
                        )
                    )
                )
            )
        )
    );

    /**
     * Intercepts addNotification, checks for duplicates against the current
     * store state, and dispatches either addNotificationSuccess or
     * addNotificationDuplicate accordingly.
     *
     * Duplication is determined by matching module + category + title + description.
     */
    // addNotification$ = createEffect(() =>
    //     this._actions$.pipe(
    //         ofType(NotificationActions.addNotification),
    //         withLatestFrom(this._store.select(selectAllNotifications)),
    //         map(([{ notification }, existing]) => {
    //             const isDuplicate = existing.some(
    //                 (n) =>
    //                     n.module === notification.module &&
    //                     n.category === notification.category &&
    //                     n.title === notification.title &&
    //                     n.description === notification.description
    //             );

    //             if (isDuplicate) {
    //                 return NotificationActions.addNotificationDuplicate();
    //             }

    //             const entity: NotificationEntity = {
    //                 ...notification,
    //                 id: crypto.randomUUID(),
    //                 read: false,
    //                 time: new Date().toISOString(),
    //             };

    //             return NotificationActions.addNotificationSuccess({
    //                 notification: entity,
    //             });
    //         })
    //     )
    // );

    updateNotification$ = createEffect(() =>
        this._actions$.pipe(
            ofType(NotificationActions.updateNotification),
            switchMap((action) =>
                this._notificationApiService
                    .update(action.id, action.data)
                    .pipe(
                        map((res) => {
                            return NotificationActions.updateNotificationSuccess(
                                {
                                    data: {
                                        ...res.data,
                                        id: String(res.data.id),
                                    },
                                    success:
                                        'Notification updated successfully',
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                NotificationActions.updateNotificationFailure({
                                    error:
                                        error?.error?.message ??
                                        'Unknown error',
                                })
                            )
                        )
                    )
            )
        )
    );

    deleteNotification$ = createEffect(() =>
        this._actions$.pipe(
            ofType(NotificationActions.deleteNotification),
            switchMap((action) =>
                this._notificationApiService.delete(action.id).pipe(
                    map(() => {
                        return NotificationActions.deleteNotificationSuccess({
                            id: action.id,
                            success: 'Notification deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            NotificationActions.deleteNotificationFailure({
                                error: error?.error?.message ?? 'Unknown error',
                            })
                        )
                    )
                )
            )
        )
    );

    // -----------------------------------------------------------
    // @ Custom effects
    // -----------------------------------------------------------

    createMismatchNotification$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(SaleActions.validateSalesConsistencySuccess),
                tap(({ consistencies }: { consistencies: Consistencies }) => {
                    this._notificationBuilder.buildSalesNotification(
                        consistencies
                    );
                })
            ),
        { dispatch: false }
    );

    // ------------------------------------------------------------------------------------------
    // @ Alerts
    // ------------------------------------------------------------------------------------------

    onNotificationActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    // NotificationActions.createNotificationSuccess,
                    // NotificationActions.updateNotificationSuccess,
                    NotificationActions.deleteNotificationSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onNotificationActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    NotificationActions.loadNotificationsFailure,
                    NotificationActions.loadNotificationFailure,
                    NotificationActions.createNotificationFailure,
                    NotificationActions.updateNotificationFailure,
                    NotificationActions.deleteNotificationFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
